import { AtSign, Loader2, Send } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
	BRAND_MENTION_PLATFORMS,
	PLATFORM_LABELS,
} from '@/types/brandMentions'
import { previewMentions } from '@/components/brand-mentions/previewMentions'

function BlurredText({
	children,
	className,
}: {
	children: string
	className?: string
}) {
	return (
		<span
			className={cn('blur-[6px] select-none', className)}
			aria-hidden="true">
			{children}
		</span>
	)
}

export function BrandMentionsScanningOverlay({
	canToggleEnabled,
	togglingEnabled,
	onEnabledChange,
}: {
	canToggleEnabled?: boolean
	togglingEnabled?: boolean
	onEnabledChange?: (enabled: boolean) => void
}) {
	const { t, i18n } = useTranslation()
	const isFrench = Boolean(i18n.language?.startsWith('fr'))
	const mentions = previewMentions(isFrench)
	const selected = mentions[0]
	const platforms = BRAND_MENTION_PLATFORMS

	return (
		<div className="absolute inset-0 z-30 overflow-hidden bg-background">
			<div className="flex h-full pointer-events-none select-none" aria-hidden="true">
				<aside className="flex h-full w-full flex-col border-r border-border/60 bg-background md:w-[340px] md:min-w-[340px] lg:w-[380px] lg:min-w-[380px]">
					<div className="border-b border-border/60 bg-card/30 px-4 py-4">
						<div className="mb-3 flex items-center gap-2">
							<AtSign className="h-5 w-5 shrink-0 text-primary" />
							<p className="truncate text-xl font-semibold tracking-tight md:text-2xl">
								{t('brandMentions.title')}
							</p>
						</div>
						<div className="flex flex-wrap gap-2">
							{platforms.map((platform) => (
								<span
									key={platform}
									className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-sm font-medium text-muted-foreground">
									<PlatformIcon
										platform={platform}
										className="h-3.5 w-3.5"
										colored
									/>
									{PLATFORM_LABELS[platform]}
								</span>
							))}
						</div>
					</div>
					<div className="min-h-0 flex-1 overflow-hidden">
						<div className="space-y-2 px-2 py-2">
							{mentions.map((mention, index) => (
								<div
									key={`${mention.platform}-${index}`}
									className={cn(
										'w-full rounded-xl border p-4 text-left',
										'bg-background border-border',
										index === 0 &&
											'bg-primary/10 border-primary/50 shadow-sm ring-1 ring-primary/30'
									)}>
									<div className="flex items-start gap-3">
										<div
											className={cn(
												'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted ring-2 ring-transparent',
												index === 0 && 'ring-primary/30'
											)}>
											<PlatformIcon
												platform={mention.platform}
												className="h-5 w-5"
												colored
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="mb-1 flex items-center justify-between gap-2">
												<span className="min-w-0 truncate text-sm font-medium text-foreground">
													<BlurredText>{mention.author}</BlurredText>
												</span>
												<span className="text-xs font-medium text-muted-foreground">
													{mention.time}
												</span>
											</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												<BlurredText>{mention.title || mention.text}</BlurredText>
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</aside>
				<main className="hidden min-w-0 flex-1 flex-col overflow-hidden bg-muted/20 md:flex">
					<div className="flex h-full flex-col overflow-hidden bg-background">
						<header className="flex-shrink-0 border-b bg-background/95 px-3 py-3 sm:px-4">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
									<PlatformIcon
										platform={selected.platform}
										className="h-5 w-5"
										colored
									/>
								</div>
								<div className="min-w-0 flex-1">
									<p className="truncate text-base font-semibold">
										<BlurredText>{selected.author}</BlurredText>
									</p>
									<div className="flex items-center gap-2 text-xs text-muted-foreground">
										<span>{PLATFORM_LABELS[selected.platform]}</span>
										<span>•</span>
										<span>{selected.time}</span>
									</div>
								</div>
							</div>
						</header>
						<div className="flex-1 overflow-hidden p-4 sm:p-6">
							<article className="mx-auto max-w-3xl space-y-3">
								<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
									<BlurredText>{selected.text}</BlurredText>
								</p>
								<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
									<BlurredText>{mentions[1].text}</BlurredText>
								</p>
							</article>
						</div>
						<footer className="flex-shrink-0 border-t bg-background/95 p-3 sm:p-4">
							<div className="flex items-center gap-3 rounded-xl border-2 border-border/40 bg-muted/30 px-4 py-3">
								<span className="min-w-0 flex-1 text-sm text-muted-foreground">
									{t('brandMentions.replyPlaceholder', {
										platform: PLATFORM_LABELS[selected.platform],
									})}
								</span>
								<Button size="icon" className="h-9 w-9 shrink-0 shadow-md" tabIndex={-1}>
									<Send className="h-4 w-4" />
								</Button>
							</div>
						</footer>
					</div>
				</main>
			</div>
			<div className="absolute inset-0 z-10 flex flex-col bg-background/55 backdrop-blur-[2px]">
				{canToggleEnabled && onEnabledChange && (
					<div className="flex justify-end px-4 py-4">
						<Switch
							checked
							disabled={togglingEnabled}
							onCheckedChange={onEnabledChange}
							aria-label={t('brandMentions.enableLabel')}
						/>
					</div>
				)}
				<div className="flex flex-1 flex-col items-center justify-center">
					<Loader2 className="h-8 w-8 animate-spin text-primary" />
					<p className="mt-3 text-sm font-medium text-foreground">
						{t('brandMentions.scanning')}
					</p>
				</div>
			</div>
		</div>
	)
}
